import React, { useEffect, useMemo, useState } from "react";

import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { Translate } from "@/app/application/Translate";


type Props = {
	name: string;
	id?: string;
	label?: string;
	type?: string;
	fullWidth?: boolean;
	placeholder?: string;
	suggestions?: string[]; // optional list of suggested tags
	maxTags?: number;
	freeSolo?: boolean;
	disabled?: boolean;
};

function normalizeTag(t: unknown) {
	if (t === null || t === undefined) return "";
	return String(t).trim();
}

const CustomextAreaArray: React.FC<Props> = ({
	name,
	id,
	label = "",
	fullWidth = false,
	placeholder = "Add entity and press Enter",
	suggestions = [],
	maxTags,
	freeSolo = true,
	disabled = false,
}) => {
	const [field, meta, helpers] = useField<string[]>({ name });
	const { setFieldTouched } = useFormikContext();
	const [inputValue, setInputValue] = useState("");
	const currentTags = (field.value ?? []).filter(Boolean).map((t) => String(t));

	// dedupe suggestions and current tags for display
	const options = useMemo(() => {
		const set = new Set<string>();
		(suggestions || []).forEach((s) => {
			if (s) set.add(String(s));
		});
		currentTags.forEach((t) => set.add(String(t)));
		return Array.from(set);
	}, [suggestions, currentTags]);

	// When formik value changes externally, keep local input in sync (no-op)
	useEffect(() => {
		// ensure tags are strings and trimmed
		const cleaned = (field.value ?? []).map(normalizeTag).filter(Boolean);
		// replace only if different
		if (JSON.stringify(cleaned) !== JSON.stringify(currentTags)) {
			helpers.setValue(cleaned);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // run once on mount

	// Add tags from a string (handle comma separated paste)
	const addTagsFromString = (value: string) => {
		if (!value) return;
		// split on comma or semicolon or whitespace+comma combos (robust)
		const parts = value
			.split(/[,\n;]+/)
			.map(normalizeTag)
			.filter(Boolean);
		if (!parts.length) return;
		const merged = Array.from(new Set([...currentTags, ...parts]));
		if (typeof maxTags === "number") {
			helpers.setValue(merged.slice(0, maxTags));
		} else {
			helpers.setValue(merged);
		}
		setInputValue("");
		setFieldTouched(name, true, false);
	};

	return (
		<Box>
			<Autocomplete
				multiple
				freeSolo={freeSolo}
				options={options}
				value={currentTags}
				inputValue={inputValue}
				filterSelectedOptions
				disabled={disabled}
				onChange={(_, newValue) => {
					// newValue can include objects if not strings — normalize
					const cleaned = (newValue ?? []).map(normalizeTag).filter(Boolean);
					// dedupe while preserving order
					const dedup = Array.from(new Set(cleaned));
					if (typeof maxTags === "number") {
						helpers.setValue(dedup.slice(0, maxTags));
					} else {
						helpers.setValue(dedup);
					}
					setInputValue("");
					setFieldTouched(name, true, false);
				}}
				onInputChange={(_, newInput, reason) => {
					// reason === "reset" when selecting an option; avoid overwriting
					if (reason === "reset") {
						setInputValue("");
						return;
					}
					setInputValue(newInput);
				}}
				renderTags={(value: string[], getTagProps) =>
					value.map((option: string, index: number) => (
						<Chip
							variant="outlined"
							label={option}
							{...getTagProps({ index })}
							key={`${option}-${index}`}
						/>
					))
				}
				renderInput={(params) => (
					<TextField
						{...params}
						id={id}
						label={label}
						placeholder={placeholder}
						onKeyDown={(e) => {
							// Add on Enter
							if (e.key === "Enter") {
								// prevent form submit in case inside <Form>
								e.preventDefault();
								if (inputValue.trim()) addTagsFromString(inputValue);
							}
							// Add on comma
							if (e.key === "," || e.key === ";") {
								e.preventDefault();
								if (inputValue.trim()) addTagsFromString(inputValue);
							}
							// Backspace behavior: when input empty remove last tag
							if (e.key === "Backspace" && !inputValue && currentTags.length) {
								e.preventDefault();
								const newTags = [...currentTags];
								newTags.pop();
								helpers.setValue(newTags);
							}
						}}
						onPaste={(e) => {
							const paste = e.clipboardData.getData("Text");
							if (paste && /[,;\n]/.test(paste)) {
								e.preventDefault();
								addTagsFromString(paste);
							}
						}}
						error={Boolean(meta.touched && meta.error)}
						helperText={meta.touched && meta.error ? String(meta.error) : ""}
						fullWidth={fullWidth}
						size="small"
					/>
				)}
			/>
			<Box sx={{ mt: 0.5 }}>
				<Typography variant="caption" sx={{ color: "text.secondary" }}>
					<Translate String="Auto.Shared.Forms.CustomTextareaArray.L162C68.AddEntitiesWithEnterOrComma">Add entities with Enter or comma. Click chip × to remove.</Translate>
				</Typography>
			</Box>
		</Box>
	);
};

export default CustomextAreaArray;
